import { Box, Center, Flex, Spinner, Switch, Text } from "@chakra-ui/react";
import api from "api/api";
import { GetLayout } from "components/get-layout";
import { InfoBalloon } from "components/info-balloon";
import { ButtonWithLoading } from "components/mailing-components/mailing-table/icon-button-with-loading";
import { openModalConfirm } from "components/modal-confirm-new";
import { ModalFieldsOptions, openModalFields } from "components/modal-fields";
import { PageTitle } from "components/title-with-back-button";

import { Toast } from "components/toast";
import { SmAddIcon } from "components/vectors/smaller-add-icon";
import { useApplicationContext } from "contexts/ApplicationContext";
import { useEffect, useState } from "react";
import { RoutePermissionFunction } from "router-manager/parts";

interface TabulacaoListItemProps {
  nome: string;
  systemTag: boolean;
  id?: number;
  ativo: boolean;
  dataCriacao: string;
  dataAtualizacao: null | string;
}

export default function ConfigAtendimento() {
  const [isLoading, setIsLoading] = useState(false);
  const [tabulacaoList, setTabulacaoList] = useState<TabulacaoListItemProps[]>(
    []
  );
  const { user } = useApplicationContext();
  const customerId = user.userData.customerId;
  // const filteredPersonalizeds = tabulacaoList.filter(
  //   (tabulacao) => tabulacao.systemTag === false
  // );

  const tabulacaoListSystemTagFalse = tabulacaoList.filter(
    (tabulacao) => tabulacao.systemTag === false
  );
  const isDisabledAddMoreTab = tabulacaoListSystemTagFalse.length >= 10;

  const handleCreateTab = async (params: {
    nome: string;
    ativo: boolean;
    systemTag: boolean;
  }) => {
    params.systemTag = params.systemTag ?? false;
    setIsLoading(true);
    try {
      const { data } = await api.post("/tags", params);
      setTabulacaoList((prev) => [...prev, data]);
    } catch (e: any) {
      const msgInterface = e.response?.data?.msgInterface;
      Toast({
        title: `Erro ao criar Tabulação`,
        description: msgInterface || ""
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateName = async ({
    id,
    nome
  }: {
    nome: string;
    id: number;
  }) => {
    const body = { nome };
    setIsLoading(true);
    try {
      const { data } = await api.put(`/tags/${id}/name`, body);

      setTabulacaoList((prev) =>
        prev.map((item) => {
          if (item.id === id) {
            item.nome = nome;
            return item;
          }
          return item;
        })
      );
    } catch (e: any) {
      const data = e.response?.data;
      const tag = data?.tag;
      const msgInterface = data?.msgInterface;
      if (tag === "error_tag_change_name") {
        openModalConfirm({
          message: (
            <Box>
              {msgInterface || ""} <br /> <br />
              Deseja atualizar mesmo assim?
            </Box>
          ),
          onConfirm: async () => {
            try {
              const { data } = await api.put(`/tags/${id}/name`, body, {
                params: { confirmation: true }
              });
              setTabulacaoList((prev) =>
                prev.map((item) => {
                  if (item.id === id) {
                    item.nome = nome;
                    return item;
                  }
                  return item;
                })
              );
            } catch (e: any) {
              const msgInterface = e.response?.data?.msgInterface;
              Toast({
                title: `Erro ao atualizar Tabulação`,
                description: msgInterface || ""
              });
            }
          }
        });
      } else {
        Toast({
          title: `Erro ao atualizar Tabulação`,
          description: msgInterface || ""
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteTag = async ({ id }: { id: number }) => {
    setIsLoading(true);
    try {
      const { data } = await api.delete(`/tags/${id}/delete`);

      setTabulacaoList((prev) => prev.filter((item) => item.id !== id));
    } catch (e: any) {
      const data = e.response?.data;
      const tag = data?.tag;
      const msgInterface = data?.msgInterface;
      if (tag === "tag_atribuida_atendimento") {
        openModalConfirm({
          message: (
            <Box>
              {msgInterface || ""} <br /> <br />
              Deseja excluir mesmo assim?
            </Box>
          ),
          onConfirm: async () => {
            try {
              const { data } = await api.delete(`/tags/${id}/delete`, {
                params: { confirmation: true }
              });
              setTabulacaoList((prev) => prev.filter((item) => item.id !== id));
            } catch (e: any) {
              const msgInterface = e.response?.data?.msgInterface;
              Toast({
                title: `Erro ao excluir Tabulação`,
                description: msgInterface || ""
              });
            }
          }
        });
      } else {
        Toast({
          title: `Erro ao excluir Tabulação`,
          description: msgInterface || ""
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateTab = async ({
    ativo,
    id
  }: {
    ativo: boolean;
    id: number;
  }) => {
    const body = { ativo };
    setIsLoading(true);
    try {
      const { data } = await api.put(`/tags/${id}/change`, body);

      setTabulacaoList((prev) =>
        prev.map((item) => {
          if (item.id === id) {
            item.ativo = item.ativo ? false : true;
            return item;
          }
          return item;
        })
      );
    } catch (e: any) {
      const msgInterface = e.response?.data?.msgInterface;
      Toast({
        title: `Erro ao ativar/desativar Tabulação`,
        description: msgInterface || ""
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getTags = async () => {
    setIsLoading(true);
    try {
      const { data } = await api.get<TabulacaoListItemProps[]>("/tags");
      setTabulacaoList(data);
    } catch (e: any) {
      const msgInterface = e.response?.data?.msgInterface;
      Toast({
        title: `Erro ao obter tabulações`,
        description: msgInterface || ""
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getTags();
  }, []);

  return (
    <GetLayout>
      <PageTitle title="Configuração Tags/Tabulações" />
      <InfoBalloon mb="15px">
        <Box mb="8px" fontWeight="bold">
          Configurações de tabulação
        </Box>
        Permite criar até 10 tabulações personalizadas por parceiro, além das
        tabulações padrão do sistema, para organizar e classificar os
        atendimentos.
      </InfoBalloon>

      <Box
        w="100%"
        bg="#fff"
        p="15px 20px"
        borderRadius="10px"
        border="1px solid var(--chakra-colors-gray-200)"
      >
        <Flex
          justifyContent="space-between"
          w="100%"
          alignItems="center"
          mb="15px"
        >
          <Text fontSize="18px" fontWeight="semibold">
            Tabulações
          </Text>
          <ButtonWithLoading
            leftIcon={<SmAddIcon />}
            isDisabled={isDisabledAddMoreTab}
            tooltipMessage={
              isDisabledAddMoreTab
                ? "Máximo de 5 tabulações personalizadas"
                : undefined
            }
            onClick={async () => {
              const isVisibleSystemTag =
                user.userData.customerId === 1 &&
                [1, 7215, 11031].includes(user.userData.userId!);

              const systemTagField:
                | ModalFieldsOptions["paramsSettings"]["params"][0]
                | undefined = isVisibleSystemTag
                ? {
                    name: "",
                    field: "systemTag",
                    type: "switch",
                    fieldProps: {
                      Box: { display: "flex", justifyContent: "flex-start" },
                      Switch: {
                        children: "Tag do sistema",
                        alignItems: "center",
                        display: "flex",
                        cursor: "pointer"
                      },
                      titleProps: { mb: "0px" }
                    },
                    initialValue: false
                  }
                : undefined;

              openModalFields({
                title: "Criar Tabulação",
                paramsSettings: {
                  params: [
                    { name: "Tabulação", field: "name" },
                    ...(isVisibleSystemTag ? [systemTagField!] : [])
                  ]
                },
                simpleValidation: true,
                onSubmit: async (param) => {
                  const nome = param.name.trim();
                  if (nome) {
                    handleCreateTab({
                      nome,
                      ativo: false,
                      systemTag: param.systemTag
                    });
                  }
                }
              });
            }}
          >
            Nova Tabulação
          </ButtonWithLoading>
        </Flex>
        <Box w="100%">
          {isLoading ? (
            <Center w="100%" h="120px">
              <Spinner />
            </Center>
          ) : (
            <>
              {tabulacaoList.map((tabulacao, i) => {
                const isEditMode =
                  !tabulacao.systemTag ||
                  (user.userData.customerId === 1 &&
                    [1, 7215, 11031].includes(user.userData.userId!));
                const isLast = i + 1 === tabulacaoList.length;
                return (
                  <Flex
                    w="100%"
                    justifyContent="space-between"
                    alignItems="center"
                    borderBottom={
                      !isLast
                        ? "1px solid var(--chakra-colors-gray-200)"
                        : undefined
                    }
                    h="70px"
                  >
                    <Box>
                      <Flex alignItems="center">
                        <Text
                          fontWeight="semibold"
                          mr="16px"
                          fontSize="16px"
                          mb=""
                        >
                          {tabulacao.nome}
                        </Text>
                        {tabulacao.systemTag ? (
                          <Center
                            p="2px 4px"
                            borderRadius="3px"
                            bg="gray.100"
                            border="1px solid var(--chakra-colors-gray-200)"
                            fontWeight="semibold"
                            fontSize="12px"
                          >
                            {"PADRÃO"}
                          </Center>
                        ) : null}
                      </Flex>

                      {tabulacao.systemTag ? (
                        <Text color="gray.600" fontSize="12px" mt="4px">
                          Criado pelo sistema
                        </Text>
                      ) : null}
                    </Box>
                    <Flex alignItems="center">
                      <Switch
                        onChange={(e) => {
                          handleUpdateTab({
                            ativo: e.target.checked,
                            id: tabulacao.id!
                          });
                        }}
                        colorScheme="green"
                        isChecked={tabulacao.ativo}
                        mr="8px"
                        fontWeight="medium"
                        color={tabulacao.ativo ? "green.500" : "gray.500"}
                        display="flex"
                        alignItems="center"
                        fontSize="14px"
                        lineHeight={1.2}
                      >
                        {tabulacao.ativo ? "Ativo" : "Inativo"}
                      </Switch>

                      {isEditMode ? (
                        <>
                          <Text
                            fontWeight="medium"
                            mr="8px"
                            cursor="pointer"
                            color="blue.400"
                            _hover={{ textDecoration: "underline" }}
                            fontSize="14px"
                            lineHeight={1.2}
                            onClick={() => {
                              openModalFields({
                                title: "Editar Tabulação",
                                paramsSettings: {
                                  params: [
                                    {
                                      name: "Tabulação",
                                      field: "name",
                                      initialValue: tabulacao.nome
                                    }
                                  ]
                                },
                                simpleValidation: true,
                                onSubmit: async (param) => {
                                  if (param.name.trim())
                                    handleUpdateName({
                                      id: tabulacao.id!,
                                      nome: param.name.trim()
                                    });
                                }
                              });
                            }}
                          >
                            Editar
                          </Text>
                          <Text
                            fontWeight="medium"
                            cursor="pointer"
                            color="red.500"
                            fontSize="14px"
                            _hover={{ textDecoration: "underline" }}
                            lineHeight={1.2}
                            onClick={() => {
                              openModalConfirm({
                                title: "Excluir Tabulação",
                                message: `Tem certeza que deseja excluir a tabulação "${tabulacao.nome}"? Esta ação não pode ser desfeita.`,
                                onConfirm: () => {
                                  handleDeleteTag({ id: tabulacao.id! });
                                }
                              });
                            }}
                          >
                            Excluir
                          </Text>
                        </>
                      ) : null}
                    </Flex>
                  </Flex>
                );
              })}
            </>
          )}
        </Box>
      </Box>
    </GetLayout>
  );
}

export const setRoutePermissions: RoutePermissionFunction = (route) => {
  route.setUserTypesPermission([
    "SUPER",
    "REGIONAL",
    "COMERCIAL",
    "PARCEIRO_GERENTE_ESTRATEGICO",
    "PARCEIRO_GERENTE_VAREJO",
    "PARCEIRO_MASTER",
    // "PARCEIRO_VENDEDOR",
    // "PARCEIRO_OPERACIONAL",
    "ASCOM"
  ]);
  route.setColomboUserTypesPermission([
    "PARCEIRO_GERENTE_ESTRATEGICO",
    "PARCEIRO_GERENTE_VAREJO",
    "PARCEIRO_MASTER"
    // "PARCEIRO_VENDEDOR"
  ]);
};
